import { Injectable } from '@angular/core';
import {
	HttpInterceptor,
	HttpRequest,
	HttpHandler,
	HttpEvent,
} from '@angular/common/http';
import { Observable, from } from 'rxjs';
import { switchMap, take } from 'rxjs/operators';
import { AngularFireAuth } from '@angular/fire/compat/auth';
import { currentVersion, environment } from 'src/environments/environment';
import LogRocket from 'logrocket';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
	/**
	 * Intercepts HTTP requests and adds an authorization token to the request headers if the request is made to a specific API base URL.
	 * @param authFire - An instance of `AngularFireAuth` used to retrieve the current user's authorization token.
	 */
	constructor(private authFire: AngularFireAuth) {}

	/**
	 * Intercepts an HTTP request and adds an authorization token to the request headers if the request is made to a specific API base URL.
	 * @param req - The intercepted HTTP request.
	 * @param next - The next HTTP handler in the interceptor chain.
	 * @returns An observable of the HTTP event.
	 */
	intercept<T>(
		req: HttpRequest<T>,
		next: HttpHandler,
	): Observable<HttpEvent<T>> {
		if (
			!req.url.includes(environment.api.baseUrl) &&
			!req.url.includes(environment.api.apiLargeUrl)
		) {
			return next.handle(req);
		}

		return from(
			this.authFire.currentUser.then((user) => user?.getIdToken(false)),
		).pipe(
			take(1),
			switchMap((token) => {
				const authReq = token
					? req.clone({
							setHeaders: {
								Authorization: `Bearer ${token}`,
								'Closer-Version': currentVersion,
								'X-LogRocket-Session-URL':
									LogRocket.sessionURL ?? 'Not Available',
							},
						})
					: req;

				return next.handle(authReq);
			}),
		);
	}
}
